import type { FC } from 'react';
import { useEffect, useMemo, useState } from 'react';
import type { TableProps } from 'antd';
import { Button, Flex, Table as AntdTable, Tooltip, Typography } from 'antd';
import { ExportOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import type { KnowledgeBaseInfo } from '@shared/api';
import { useGetKnowledgeListKnowledgeListGetQuery } from '@shared/api';
import { authSelectors } from '@shared/redux/ducks/auth';
import { useAppSelector } from '@shared/redux';
import { LoadingState } from '@shared/ui/loading-state/loading-state';
import { FetchingErrorState } from '@shared/ui/fetching-error-state/fetching-error-state';
import { Text } from '@shared/ui/antd';
import { DateService } from '@shared/services/date';
import { useAgentKnowledgeBase } from '@modules/ai-agent/hooks/use-agent-knowledge-base';
import { useToggle } from '@shared/hooks/use-toggle';
import { KnowledgeBaseCreateModal } from '@modules/knowledge-base/ui/ui/create-modal/create-modal';
import { parseKnowledgeLink } from '@modules/knowledge-base/lib/parse-link';

import styles from '../../prompt.module.scss';

interface KnowledgeBaseSectionProps {
  agentId: number;
  selectedKnowledgeIds: number[];
}

const areSetsEqual = (first: Set<number>, second: Set<number>) => {
  if (first.size !== second.size) return false;

  for (const value of first) {
    if (!second.has(value)) return false;
  }

  return true;
};

const mapTypeLabel = (
  type: KnowledgeBaseInfo['data_type'],
  t: TFunction<'translation', 'prompt'>,
) => {
  switch (type) {
    case 'file':
      return t('knowledgeBase.types.file');
    case 'link':
      return t('knowledgeBase.types.link');
    case 'text':
      return t('knowledgeBase.types.text');
    case 'linked_document':
      return t('knowledgeBase.types.linkedDocument');
    default:
      return type;
  }
};

export const KnowledgeBaseSection: FC<KnowledgeBaseSectionProps> = ({
  agentId,
  selectedKnowledgeIds,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'prompt' });
  const currentOrganization = useAppSelector(authSelectors.selectCurrentOrganizationInfo);
  const { updateKnowledgeBase, isUpdating } = useAgentKnowledgeBase();
  const {
    state: isCreateKnowledgeBaseModalOpen,
    setTrue: openCreateKnowledgeBaseModal,
    setFalse: closeCreateKnowledgeBaseModal,
  } = useToggle();

  const { data, isLoading, isError, refetch } = useGetKnowledgeListKnowledgeListGetQuery(
    {
      organizationId: currentOrganization.id,
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !currentOrganization?.id,
    },
  );

  const [checkedIds, setCheckedIds] = useState<Set<number>>(new Set());
  const initialCheckedIds = useMemo(() => new Set(selectedKnowledgeIds), [selectedKnowledgeIds]);

  useEffect(() => {
    setCheckedIds(new Set(selectedKnowledgeIds));
  }, [selectedKnowledgeIds]);

  const hasChanges = !areSetsEqual(checkedIds, initialCheckedIds);
  const shouldShowCreateButton = selectedKnowledgeIds.length === 0;

  const rows = useMemo(() => data ?? [], [data]);
  const columns = useMemo<TableProps<KnowledgeBaseInfo>['columns']>(
    () => [
      {
        title: t('knowledgeBase.table.name'),
        dataIndex: 'title',
        key: 'title',
        width: 320,
        render: (title: KnowledgeBaseInfo['title'], knowledgeBase) => {
          const documentLink =
            knowledgeBase.data_type === 'linked_document' && knowledgeBase.link
              ? parseKnowledgeLink(knowledgeBase.link)
              : null;

          if (!documentLink) {
            return (
              <Tooltip title={title} placement="topLeft">
                <Text
                  variant="sm"
                  color="var(--color-text-primary)"
                  className={styles.knowledgeTitleText}
                >
                  {title}
                </Text>
              </Tooltip>
            );
          }

          return (
            <Tooltip title={title} placement="topLeft">
              <Typography.Link
                className={styles.knowledgeDocumentLink}
                href={documentLink.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${t('knowledgeBase.openDocument')}: ${title}`}
              >
                <span className={styles.knowledgeTitleText}>{title}</span>
                <ExportOutlined aria-hidden />
              </Typography.Link>
            </Tooltip>
          );
        },
      },
      {
        title: t('knowledgeBase.table.type'),
        dataIndex: 'data_type',
        key: 'data_type',
        width: 180,
        render: (type: KnowledgeBaseInfo['data_type']) => (
          <Text variant="sm" color="var(--color-text-tertiary)">
            {mapTypeLabel(type, t)}
          </Text>
        ),
      },
      {
        title: t('knowledgeBase.table.date'),
        dataIndex: 'created_at',
        key: 'created_at',
        width: 180,
        render: (createdAt: KnowledgeBaseInfo['created_at']) => (
          <Text variant="sm" color="var(--color-text-tertiary)">
            {DateService.format(createdAt, 'DD.MM.YYYY, HH:mm')}
          </Text>
        ),
      },
    ],
    [t],
  );
  const rowSelection = useMemo<TableProps<KnowledgeBaseInfo>['rowSelection']>(
    () => ({
      selectedRowKeys: [...checkedIds],
      preserveSelectedRowKeys: true,
      getCheckboxProps: () => ({
        disabled: isUpdating,
      }),
      onChange: (selectedRowKeys) => {
        setCheckedIds(new Set(selectedRowKeys.map((key) => Number(key))));
      },
    }),
    [checkedIds, isUpdating],
  );

  const handleUpdate = async () => {
    try {
      await updateKnowledgeBase(agentId, [...checkedIds]);
    } catch {
      setCheckedIds(new Set(selectedKnowledgeIds));
    }
  };

  return (
    <Flex vertical className={styles.element} gap={12}>
      <Flex gap={4} align="start" vertical>
        <Text variant="md" fontWeight={500}>
          {t('knowledgeBase.title')}
        </Text>

        <Text variant="sm" style={{ color: 'var(--color-text-tertiary)' }}>
          {t('knowledgeBase.description')}
        </Text>
      </Flex>

      {isLoading && <LoadingState />}
      {!isLoading && isError && <FetchingErrorState refetch={refetch} />}

      {!isLoading && !isError && (
        <>
          <div className={styles.knowledgeTable}>
            <AntdTable<KnowledgeBaseInfo>
              columns={columns}
              dataSource={rows}
              pagination={false}
              rowKey="id"
              rowSelection={rowSelection}
              scroll={{ x: 720, y: 400 }}
              tableLayout="fixed"
            />
          </div>
          <Flex gap={8}>
            <Button
              type="primary"
              size="large"
              loading={isUpdating}
              disabled={!hasChanges}
              onClick={handleUpdate}
            >
              {t(
                shouldShowCreateButton
                  ? 'knowledgeBase.createButton'
                  : 'knowledgeBase.updateButton',
              )}
            </Button>
            <Button type="default" size="large" onClick={openCreateKnowledgeBaseModal}>
              {t('knowledgeBase.addButton')}
            </Button>
          </Flex>
        </>
      )}

      <KnowledgeBaseCreateModal
        isOpen={isCreateKnowledgeBaseModalOpen}
        onClose={closeCreateKnowledgeBaseModal}
      />
    </Flex>
  );
};
